import classnames from 'classnames/bind';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { DeployRoute } from 'application/types/routes';

import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';

import { CameraDetail } from 'cameras/views/CameraDetail/CameraDetail';
import { CamerasOverview } from 'cameras/views/CamerasOverview/CamerasOverview';
import { CreateFilesView } from 'files/views/CreateFilesView/CreateFilesView';
import { DeploymentDetail } from 'deployments/views/DeploymentDetail/DeploymentDetail';
import { DeploymentOverview } from 'deployments/views/DeploymentOverview/DeploymentOverview';
import { Drawer } from 'components/Drawer/DrawerDeprecated';
import { EditDeploymentForm } from 'deployments/components/Form/EditForm';
import { EmptyView } from 'components/EmptyView/EmptyView';
import { SplitView } from 'components/SplitView/SplitView';
import { useDeployment } from 'deployments/hooks/useDeployment';
import { EventsOverview } from 'events/views/EventsOverview/EventsOverview';
import { FileDetail } from 'files/views/FileDetail/FileDetail';
import { FilesOverviewTable } from 'files/views/FilesOverview/Table/Table';
import { GatewayDetail } from 'gateways/views/GatewayDetail/GatewayDetail';
import { GatewayOverview } from 'gateways/views/GatewayOverview/GatewayOverview';
import { InputStreamsOverview } from 'streams/views/InputStreamsOverview/InputStreamsOverview';
import { TrackableListsOverview } from 'trackables/views/TrackableListsOverview/TrackableListsOverview';
import { TrackableListDetail } from 'trackables/views/TrackableListDetail/TrackableListDetail';
import { DetailRouteWithTrackableItemRef } from './DetailRouteWithTrackableItemRef';
import {
  deploymentFormDrawerClassName,
  NewDeploymentForm,
} from 'deployments/components/Form';
import {
  RapidDeploy,
  RapidDeployDeployments,
  RapidDeployGatewaysList,
  RapidDeployRoute,
  RapidDeployVideoSources,
} from 'deployments/views/RapidDeploy';
import { StreamDetail } from 'streams/views/StreamDetail/StreamDetail';

import EmptyDeploymentsIllustration from 'deployments/images/empty-pipeline-deployments.svg?react';

import { DetailRouteWithDeploymentRef } from './DetailRouteWithDeploymentRef';
import { DeploySidebar } from './Sidebar';
import styles from './Deploy.module.scss';

const c = classnames.bind(styles);

function EditDeploymentDrawer() {
  const navigate = useNavigate();
  const { deploymentID } = useParams<{ deploymentID: string }>();
  const { data: deployment, isLoading } = useDeployment(deploymentID);

  function handleClose(isOpen?: boolean) {
    if (!isOpen) {
      navigate(`/${DeployRoute.DEPLOYMENTS}`);
    }
  }

  if (isLoading || !deployment) {
    return null;
  }

  return (
    <SplitView onOpenChange={handleClose} easeOut open>
      <EditDeploymentForm
        deployment={deployment}
        startInEditMode
        onSuccess={() => handleClose(false)}
      />
    </SplitView>
  );
}

export function Deploy() {
  const navigate = useNavigate();

  const { isSimplifiedUxPlan } = useCurrentPlan();
  const defaultRoute = isSimplifiedUxPlan
    ? DeployRoute.CAMERAS
    : DeployRoute.GATEWAYS;

  return (
    <div className={c('deploy')}>
      <DeploySidebar />

      <Routes>
        <Route index element={<Navigate to={defaultRoute} replace />} />

        {/* Gateways */}
        <Route
          path={`${DeployRoute.GATEWAYS}/*`}
          element={
            <>
              <GatewayOverview />
              <Outlet />
            </>
          }
        >
          <Route
            path=":gatewayID/*"
            element={
              <DetailRouteWithDeploymentRef>
                <GatewayDetail />
              </DetailRouteWithDeploymentRef>
            }
          />
        </Route>

        {/* Cameras */}
        <Route
          path={DeployRoute.CAMERAS}
          element={
            <>
              <CamerasOverview />
              <Outlet />
            </>
          }
        >
          <Route
            path=":cameraID/*"
            element={
              <DetailRouteWithDeploymentRef>
                <CameraDetail />
              </DetailRouteWithDeploymentRef>
            }
          />
        </Route>

        {/* Streams */}
        <Route
          path={DeployRoute.STREAMS}
          element={
            <>
              <InputStreamsOverview />
              <Outlet />
            </>
          }
        >
          <Route
            path=":streamID/*"
            element={
              <DetailRouteWithDeploymentRef>
                <StreamDetail />
              </DetailRouteWithDeploymentRef>
            }
          />
        </Route>

        {/* Files */}
        <Route
          path={`${DeployRoute.FILES}/*`}
          element={
            <>
              <FilesOverviewTable />
              <Outlet />
            </>
          }
        >
          <Route
            path=":fileID/*"
            element={
              <DetailRouteWithDeploymentRef>
                <FileDetail />
              </DetailRouteWithDeploymentRef>
            }
          />
        </Route>

        <Route
          path={`${DeployRoute.FILES}/new`}
          element={<CreateFilesView />}
        />

        {/* Trackables */}
        <Route
          path={DeployRoute.TRACKABLES}
          element={
            <>
              <TrackableListsOverview />
              <Outlet />
            </>
          }
        >
          <Route
            path=":trackableListId/*"
            element={
              <DetailRouteWithTrackableItemRef>
                <TrackableListDetail />
              </DetailRouteWithTrackableItemRef>
            }
          />
        </Route>

        {/* Deployments */}
        <Route
          path={`${DeployRoute.DEPLOYMENTS}/*`}
          element={<DeploymentOverview />}
        >
          <Route
            path="new"
            element={
              <Drawer
                title="Deploy workflow"
                containerClassName={deploymentFormDrawerClassName}
                onClose={() => {
                  navigate(DeployRoute.DEPLOYMENTS);
                }}
                open
              >
                <NewDeploymentForm
                  onSuccess={({ id }) =>
                    navigate(`${DeployRoute.DEPLOYMENTS}/${id}`)
                  }
                />
              </Drawer>
            }
          />
          <Route path="edit/:deploymentID" element={<EditDeploymentDrawer />} />
          <Route path=":deploymentID" element={<DeploymentDetail />} />
          <Route
            index
            element={
              <EmptyView image={EmptyDeploymentsIllustration}>
                Select a workflow deployment on the left to view its output
                streams or files, function logs, and configuration.
              </EmptyView>
            }
          />
        </Route>

        {/* Rapid deploy */}
        <Route path={`${DeployRoute.RAPID_DEPLOY}/*`} element={<RapidDeploy />}>
          <Route
            path={RapidDeployRoute.GATEWAY}
            element={<RapidDeployGatewaysList />}
          />
          <Route
            path={RapidDeployRoute.VIDEO_SOURCES}
            element={<RapidDeployVideoSources />}
          />
          <Route
            path={RapidDeployRoute.DEPLOY}
            element={<RapidDeployDeployments />}
          />
          <Route
            index
            element={<Navigate to={RapidDeployRoute.GATEWAY} replace />}
          />
        </Route>

        {/* Events */}
        <Route path={DeployRoute.EVENTS} element={<EventsOverview />} />
      </Routes>
    </div>
  );
}
